import type {
	PricingCampaignSettingsModel,
	ProductAssignmentModel,
} from '@/shared/generated'

import { useMemo } from 'react'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDebounceValue } from 'usehooks-ts'

import {
	isValidAssignments,
	useMatchedProductsOnEditPcQuery,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { Badge } from '@/shared/components/ui/badge'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { formatNumber } from '@/shared/tools/locale'

function shouldValidateAssignments(assignments: ProductAssignmentModel) {
	return (
		assignments.length > 0 &&
		assignments.every(
			({ filters }) => filters.length > 0 && filters.every((f) => f.name)
		)
	)
}
export function PricingCampaignAssignmentPrediction({
	id,
	settings,
	scenarioId,
	order,
}: {
	id: string
	settings: PricingCampaignSettingsModel
	scenarioId?: string
	order?: number
}) {
	const { t } = useTranslation()
	const queryParams = !scenarioId
		? { only_global: true }
		: {
				scenario_id: scenarioId,
			}
	const { data, isPending } = usePricingCampaignsQuery<number>(queryParams, {
		select: (data) => Math.max(...data.map((pc) => pc.order)),
	})
	const maxOrder = data
	if (!settings?.product_assignments?.length) {
		return null
	}
	if (!shouldValidateAssignments(settings.product_assignments)) {
		return null
	}
	if (!isValidAssignments(settings.product_assignments)) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<Badge variant='destructive'>
						<AlertTriangleIcon />
						{t('pc.assignment.invalid_assignments', {
							defaultValue: 'Invalid assignments',
						})}
					</Badge>
				</TooltipTrigger>
				<TooltipContent>
					{t('pc.assignment.invalid_assignments_tooltip', {
						defaultValue:
							'We can`t predict the number of matching products because some of your pricing campaigns have invalid assignments, please check them and try again.',
					})}
				</TooltipContent>
			</Tooltip>
		)
	}

	return isPending ? (
		<Badge variant='neutral'>
			<Spinner />
			<span className='font-medium text-muted-foreground'>
				{t('loading', { defaultValue: 'Loading...' })}
			</span>
		</Badge>
	) : (
		<PricingCampaignAssignmentPredictionBadge
			id={id}
			settings={settings}
			scenarioId={scenarioId}
			order={order || (maxOrder || 0) + 1}
		/>
	)
}
function PricingCampaignAssignmentPredictionBadge({
	id,
	settings,
	scenarioId,
	order,
}: {
	id: string
	settings: PricingCampaignSettingsModel
	scenarioId?: string
	order: number
}) {
	const { t } = useTranslation()

	const data = useMemo(
		() => ({
			id,
			scenario_id: scenarioId ?? null,
			settings,
			order,
		}),
		[settings, scenarioId, id, order]
	)

	const [debounced] = useDebounceValue(data, 1000)

	const matchedProductsQuery = useMatchedProductsOnEditPcQuery(debounced, {
		retry: 1,
	})
	if (matchedProductsQuery.isError) {
		return null
	}

	return matchedProductsQuery.isLoading ? (
		<Badge variant='neutral'>
			<Spinner />
			<span className='font-medium text-muted-foreground'>Loading...</span>
		</Badge>
	) : matchedProductsQuery.data ? (
		<Badge variant='neutral'>
			{t('pc.assignment.predict', {
				value: formatNumber(matchedProductsQuery.data.pricing_line_count),
				defaultValue: `${formatNumber(matchedProductsQuery.data.pricing_line_count)} matching products we have found.`,
			})}
		</Badge>
	) : null
}
